import { requestApiOfProject } from 'serviceWorker/api/requestApi';
import type { DataScope } from 'serviceWorker/helpers/dataScope';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { makeNewVersionCurrent, NewVersionSource } from 'serviceWorker/repository/documentation/setCurrentVersion';
import {
  ExportedVersionSetting,
  isExportedVersionMadeCurrent,
  type ExportedVersionOptions,
  type ExportToDocumentationResultData,
} from 'shared/domain/issue/linkedDocumentation/operations';
import { LogLevel } from 'shared/types/logger';

/*
 * What follows an export (PT-7117). The export itself succeeded by the time these run:
 * a setting that cannot be saved is logged and reported, not thrown, so the user can set it
 * in the documentation. They go to the project the export was made in, as the user may have
 * selected another one meanwhile; the device is changed only while that project is current.
 */

/** The backend names the version after the file; this is the name the user chose instead. Resolves whether it was saved. */
async function renameExportedVersion(
  { documentationId, versionId }: ExportToDocumentationResultData,
  name: string,
  projectId: string
): Promise<boolean> {
  try {
    await requestApiOfProject(projectId, {
      method: 'PUT',
      path: `documentation/${documentationId}/version/${versionId}`,
      body: { name },
    });
    return true;
  } catch (error) {
    swLog('Exported documentation version could not be renamed', LogLevel.ERROR, error, {
      entityName: 'documentations',
      projectId,
      query: { documentationId, versionId },
    });
    return false;
  }
}

/** The export does not make the version current: it is made so on request. Resolves whether it was. */
function makeExportedVersionCurrent(
  { documentationId, versionId }: ExportToDocumentationResultData,
  projectId: string,
  scope: DataScope
): Promise<boolean> {
  return makeNewVersionCurrent(
    (changes) => requestApiOfProject(projectId, { method: 'PUT', path: `documentation/${documentationId}`, body: changes }),
    { documentationId, versionId, source: NewVersionSource.export, projectId },
    (write) => scope.write(write)
  );
}

/**
 * Saves what the user chose about the exported version: its name and whether it is current.
 * Resolves the settings that could not be saved, for the user to be told.
 */
export async function saveExportedVersionSettings(
  data: ExportToDocumentationResultData,
  version: ExportedVersionOptions | undefined,
  scope: DataScope
): Promise<ExportedVersionSetting[]> {
  const { projectId } = scope;
  if (!projectId) {
    return [];
  }
  const unsaved: ExportedVersionSetting[] = [];
  if (version?.name && !(await renameExportedVersion(data, version.name, projectId))) {
    unsaved.push(ExportedVersionSetting.name);
  }
  if (isExportedVersionMadeCurrent(version) && !(await makeExportedVersionCurrent(data, projectId, scope))) {
    unsaved.push(ExportedVersionSetting.current);
  }
  return unsaved;
}
